<template>
  <div class="tab-content-inner">
    <div class="info-alert info-alert--accent">
      <CIcon name="cil-star" class="info-icon" />
      <div>
        <strong>Avaliação de atendimentos:</strong> depois que o atendimento encerra, a
        IA lê a conversa — a transcrição da ligação ou as mensagens do WhatsApp — e
        devolve uma nota geral, a percepção do cliente, a nota do atendimento e um bloco
        por critério, com o que foi bem e o que poderia melhorar, citando trechos do que
        foi dito. O resultado aparece no histórico de atendimento do cliente e no monitor
        de ligações.
        <br />
        <small>
          A nota é do <em>atendimento</em>, não do canal: a mesma régua vale para a
          ligação e para a conversa escrita, e é isso que permite acompanhar o vendedor
          independente de por onde ele atendeu. Cada atendimento avaliado consome uma
          requisição do provedor de IA.
        </small>
      </div>
    </div>

    <hr class="section-divider" />

    <div class="toggle-row">
      <label class="toggle-label" for="avaliar-atendimento-ia">
        Avaliar atendimentos com IA
        <small class="form-text text-muted">
          Desligado, nada é avaliado e nada é cobrado do provedor. A transcrição da
          ligação continua funcionando como antes — ter transcrição não significa querer
          nota.
        </small>
      </label>
      <CSwitch
        id="avaliar-atendimento-ia"
        color="primary"
        shape="pill"
        :checked="!!parametro.avaliar_atendimento_ia"
        @update:checked="update({ avaliar_atendimento_ia: $event })"
      />
    </div>

    <hr class="section-divider" />

    <div class="section-label">Critérios avaliados</div>
    <p class="form-text text-muted section-help">
      Cada critério vira um bloco com nota própria na avaliação. Sem nenhum critério
      cadastrado, a IA usa os quatro padrões de televendas: abertura e sondagem, condução
      do atendimento, negociação e valor, e fechamento. A percepção do cliente é estimada
      sempre, independente dos critérios: é a nota que ele daria ao atendimento.
    </p>

    <div v-if="criterios.length" class="criterios">
      <div v-for="(criterio, indice) in criterios" :key="indice" class="criterio-item">
        <div class="criterio-ordem">{{ indice + 1 }}</div>
        <div class="criterio-campos">
          <input
            class="form-control criterio-titulo"
            placeholder="Nome do critério (ex.: Negociação e valor)"
            :value="criterio.titulo"
            @input="alterarCriterio(indice, { titulo: $event.target.value })"
          />
          <textarea
            class="form-control criterio-descricao"
            rows="2"
            placeholder="O que a IA deve observar neste critério. Quanto mais concreto, mais consistente fica a nota."
            :value="criterio.descricao"
            @input="alterarCriterio(indice, { descricao: $event.target.value })"
          ></textarea>
        </div>
        <CButton
          variant="outline"
          color="danger"
          size="sm"
          class="criterio-remover"
          @click="removerCriterio(indice)"
        >
          <CIcon name="cil-trash" />
        </CButton>
      </div>
    </div>

    <div v-else class="sem-criterios">
      Nenhum critério próprio cadastrado — a IA usará os quatro padrões.
    </div>

    <CButton variant="outline" color="primary" size="sm" @click="adicionarCriterio">
      <CIcon name="cil-plus" /> Adicionar critério
    </CButton>

    <hr class="section-divider" />

    <div class="section-label">O que esta empresa valoriza no atendimento</div>
    <div class="form-group">
      <textarea
        class="form-control prompt-textarea"
        :value="parametro.prompt_avaliacao_atendimento"
        @input="update({ prompt_avaliacao_atendimento: $event.target.value })"
        placeholder="Ex.: Trabalhamos com obra, então agilidade na entrega vale mais que desconto. Penalize desconto concedido sem contrapartida de volume ou prazo. Valorize quem confirma o pedido item a item antes de encerrar."
      ></textarea>
      <small class="form-text text-muted">
        Serve para calibrar as notas ao que importa aqui. A régua das notas e o formato da
        resposta são fixos — este texto ajusta o julgamento, não a estrutura.
      </small>
    </div>

    <div class="form-group">
      <label>Modelo usado na avaliação</label>
      <input
        class="form-control"
        :value="parametro.model_avaliacao_atendimento"
        @input="update({ model_avaliacao_atendimento: $event.target.value })"
        placeholder="Em branco usa o modelo padrão configurado em Provedor & Modelos"
      />
      <small class="form-text text-muted">
        Avaliar exige mais julgamento que resumir. Se as notas saírem rasas ou repetidas,
        um modelo mais forte aqui costuma resolver.
      </small>
    </div>
  </div>
</template>

<script>
export default {
  name: "tab-avaliacao-atendimento",
  props: {
    parametro: {
      type: Object,
      required: true,
    },
  },
  computed: {
    criterios() {
      return Array.isArray(this.parametro.criterios_avaliacao_atendimento)
        ? this.parametro.criterios_avaliacao_atendimento
        : [];
    },
  },
  methods: {
    update(changes) {
      this.$emit("update", changes);
    },
    // A lista inteira é reemitida a cada tecla porque o pai guarda o parâmetro completo
    // e salva de uma vez — não há edição parcial de critério no servidor.
    salvarCriterios(criterios) {
      this.update({ criterios_avaliacao_atendimento: criterios });
    },
    alterarCriterio(indice, mudancas) {
      const criterios = this.criterios.map((criterio, posicao) =>
        posicao === indice ? { ...criterio, ...mudancas } : criterio
      );
      this.salvarCriterios(criterios);
    },
    adicionarCriterio() {
      this.salvarCriterios([...this.criterios, { titulo: "", descricao: "" }]);
    },
    removerCriterio(indice) {
      this.salvarCriterios(this.criterios.filter((_, posicao) => posicao !== indice));
    },
  },
};
</script>

<style scoped>
/* Interruptor com o rótulo à esquerda e a explicação embaixo dele — o mesmo arranjo dos
   booleanos das outras telas de configuração. */
.toggle-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.toggle-label {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 0;
  cursor: pointer;
}

.toggle-label .form-text {
  font-weight: 400;
  margin-top: 2px;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #3a3e81;
  margin-bottom: 6px;
}

.section-help {
  margin-bottom: 12px;
}

.section-divider {
  margin: 20px 0;
  border-top: 1px solid #dddce9;
}

.criterios {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}

.criterio-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  background: #f2f2f8;
}

.criterio-ordem {
  flex: 0 0 26px;
  height: 26px;
  border-radius: 50%;
  background: #3a3e81;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.criterio-campos {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.criterio-titulo {
  font-weight: 600;
}

.criterio-descricao {
  resize: vertical;
}

.criterio-remover {
  flex: 0 0 auto;
}

.sem-criterios {
  padding: 12px;
  border: 1px dashed #dddce9;
  border-radius: 10px;
  color: #7a7a8c;
  font-size: 13px;
  margin-bottom: 12px;
}

.prompt-textarea {
  min-height: 120px;
  resize: vertical;
}
</style>
